import React, { ReactElement } from 'react'
import { graphql, Link } from 'gatsby'

import { ProductPageQuery, MdxFrontmatterLinks } from 'generated/types/gatsby'
import { CATEGORIES, RELEASE_NOTES, WHATS_NEW } from '../categoriesSort'
import { ProductLayout } from '../components/product-layout/ProductLayout'
import { ReleaseNotes } from '../components/release-notes/ReleaseNotes'
import { WindowLocation } from '@reach/router'

interface ProductTemplateProps {
  readonly data: ProductPageQuery
  readonly location: WindowLocation // used to get hash from url and expand menu category on page load
}

const filterLinksByCat = (
  links: Array<
    Pick<
      MdxFrontmatterLinks,
      'header' | 'path' | 'pdf' | 'title' | 'top' | 'category' | 'isNew' | 'isLts'
    >
  >,
  category: string
): Array<
  Pick<
    MdxFrontmatterLinks,
    'header' | 'path' | 'pdf' | 'title' | 'top' | 'category' | 'isNew' | 'isLts'
  >
> => links.filter((link) => link.category === category)

const getGuidesLink = (link: Pick<MdxFrontmatterLinks, 'path' | 'title'>) => {
  if (link.path.match(/\.pdf$/)) {
    return (
      <a
        className={`guides-link-set__item la-icon la-icon_download`}
        href={link.path}
        key={link.title}
        download
      >
        {link.title}
      </a>
    )
  }
  return (
    <Link
      className={'guides-link-set__item la-icon la-icon_document'}
      to={link.path}
      key={link.title}
    >
      {link.title}
    </Link>
  )
}

const ProductTemplate = ({ data, location }: ProductTemplateProps) => {
  const getSection = (category: { name: string; children: string[] }) => {
    let subSections: Array<ReactElement | undefined> = []
    if (category.children && category.children.length > 0) {
      subSections = category.children
        .map((subcategory) => {
          const id = subcategory.replace(/\s/g, '')
          const filteredLinks = filterLinksByCat(data.mdx.frontmatter.links, subcategory)

          if (subcategory === RELEASE_NOTES) {
            if (data.allFile?.edges?.length > 0 || filteredLinks.length > 0)
              return (
                <ReleaseNotes
                  key={subcategory}
                  location={location}
                  subcategory={subcategory}
                  allFiles={data.allFile.edges}
                  allLinks={filteredLinks}
                  productName={data.mdx.frontmatter.title}
                />
              )
          }
          const subSectionMarkup = filteredLinks.map((link) => getGuidesLink(link))
          if (subSectionMarkup.length > 0) {
            return (
              <div className={'guides-subcategory guides-subcategory-list__item'} key={subcategory}>
                {subSectionMarkup.length > 1 && (
                  <input
                    className={'guides-subcategory__checkbox'}
                    type="checkbox"
                    name="categories"
                    value={subcategory}
                    defaultChecked={location?.hash.substring(1) === id}
                    id={id}
                  />
                )}

                {subSectionMarkup.length > 1 && (
                  <label htmlFor={id} className={'guides-subcategory__label'} id={'#' + id}>
                    {subcategory}
                  </label>
                )}

                {subSectionMarkup.length > 1 && (
                  <div className={'guides-subcategory__links guides-link-set'}>
                    {subSectionMarkup}
                  </div>
                )}

                {subSectionMarkup.length === 1 && (
                  <div className={'guides-link-set'}>{getGuidesLink(filteredLinks[0])}</div>
                )}
              </div>
            )
          }
        })
        .filter((subSection) => subSection !== undefined)
    }
    const filteredLinks = filterLinksByCat(data.mdx.frontmatter.links, category.name)
    const sectionMarkup = filteredLinks.map((link) => getGuidesLink(link))

    return (
      (subSections.length > 0 || sectionMarkup.length > 0) && (
        <div className={'guides-category guides-category-list__item'} key={category.name}>
          <div className={'guides-category__label'}>{category.name}</div>
          {filteredLinks && filteredLinks.length > 0 && category.name !== RELEASE_NOTES && (
            <div className={'guides-category__links guides-link-set'}>{sectionMarkup}</div>
          )}
          {subSections.length > 0 && (
            <div className={'guides-category__body guides-subcategory-list'}>{subSections}</div>
          )}
        </div>
      )
    )
  }

  const isFirstColumnSection = (sectionName: string) =>
    sectionName === WHATS_NEW || sectionName === RELEASE_NOTES

  const firstColumnSections = CATEGORIES.filter(
    (section) => isFirstColumnSection(section.name) && section.children?.length > 0
  )
    .map((section) => getSection(section))
    .filter((section) => !!section)

  const secondColumnSections = CATEGORIES.filter(
    (section) => !isFirstColumnSection(section.name) && section.children
  )
    .map((section) => getSection(section))
    .filter((section) => !!section)

  const top = data.mdx.frontmatter.links
    .filter((link) => link.top)
    .map((link) => (
      <Link
        className={'guides-link-set__item la-icon la-icon_document'}
        to={link.path}
        key={link.title}
      >
        {link.title}
      </Link>
    ))

  return (
    <ProductLayout productName={data.mdx.frontmatter.title} location={location}>
      <div className={`product-info`}>
        {/* ${firstColumnSections.length > 0 ? 'product-info_has-three-columns' : ''} */}
        {firstColumnSections.length > 0 && (
          <div className="product-info__guides product-info__guides_whats-new guides-category-list">
            {firstColumnSections}
          </div>
        )}

        <div className="product-info__guides guides-category-list">{secondColumnSections}</div>

        <div className="product-info__guides product-info__guides_top-documents guides-category-list">
          <div className={'guides-category'}>
            <div className="guides-category__label">Top Documents</div>

            <div className="guides-category__body guides-subcategory-list">
              <div className={'guides-subcategory guides-subcategory-list__item'}>
                <div className={'guides-link-set'}>{top}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </ProductLayout>
  )
}

export default ProductTemplate

export const query = graphql`
  query ProductPage($path: String!, $olderReleasesDir: String!) {
    mdx(fields: { slug: { eq: $path } }) {
      frontmatter {
        title
        links {
          header
          path
          pdf
          title
          top
          category
          isNew
          isLts
        }
      }
      fields {
        slug
      }
    }
    allFile(filter: { relativeDirectory: { eq: $olderReleasesDir } }) {
      edges {
        node {
          name
          relativeDirectory
        }
      }
    }
  }
`
